import { useEffect, useState, useRef, useContext } from "react";
import {
  Box,
  Center,
  Flex,
  Text,
  useDisclosure,
  useMediaQuery,
  Grid,
} from "@chakra-ui/react";
import PuffLoader from "react-spinners/PuffLoader";
import TweetsReplies from "../tweets/tweetsReplies";
import { useSession } from "next-auth/react";
import { getAccount, updateUser } from "../../utils/sessionHelper";
import { useStats } from "hooks/useStats";
import { useOnboardingSteps } from "components/onboard-panel/useOnboardingSteps";
import { formatMedia } from "utils/linkedinUtils";
import { grow } from "../../controllers/search";
import { MainContext } from "../../context/mainContext";
import { useFirebaseUser } from "../../utils/useFirebaseUser";
import { ConfirmPopup } from "components/popups/confirmPopup";
import toast from "react-hot-toast";
import { textStyle } from "theme/names";
import { FilterSortRefreshAutoLike } from "./filter-sort-refresh-autolike-panel";
import { ENGAGE_FILTER_KEY } from "./utils";
import { useMiscellaneousContext } from "context/miscellaneousContext";
import { getCachedKey, getCachedKeyTime, updateCachedKey, updateCachedKeyTime } from "utils/localforageHelpers";

const defaultFiltersValues = {
  defaultMinFollowers: 0,
  defaultMaxFollowers: 999999999,
};

let startLimitationUTCHour = 12;
let endLimitationUTCHour = 19;
let utcHours = new Date().getUTCHours();
var isPeakHour =
  utcHours >= startLimitationUTCHour && utcHours < endLimitationUTCHour;

export function BestForYouPanel({ isOrg = false, header = null as any }) {
  const { defaultMinFollowers, defaultMaxFollowers } = defaultFiltersValues;
  const { data: session, status } = useSession() ?? {};
  const loading = status === "loading";

  const [isPersonalizedSorting, setIsPersonalizedSorting] =
    useState(true);
  const [savedTweet, setSavedTweet] = useState([]);
  const [recos, setRecos] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [data, setData] = useState<any>(null);
  const [nbMinDaysOld, setNbMinDaysOld] = useState(0);
  const [nbMaxDaysOld, setNbMaxDaysOld] = useState(2);
  const [nbMinLikes, setNbMinLikes] = useState(40);
  const [nbMinRt, setNbMinRt] = useState(0);
  const [nbMinFollowers, setNbMinFollowers] =
    useState(defaultMinFollowers);
  const [nbMaxFollowers, setNbMaxFollowers] =
    useState(defaultMaxFollowers);
  const [hasCustomFilters, setHasCustomFilters] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [noEngage, setNoEngage] = useState<boolean>(false);
  const [listId, setListId] = useState<string>("");
  const [filters, setFilters] = useState<any>(defaultFiltersValues);
  const [refresh, refresher] = useState({});
  const [isTooSmall] = useMediaQuery("(max-width: 800px)");
  const { updateStatsCounter } = useStats();
  const { updateOnboardSteps } = useOnboardingSteps();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const mainContext = useContext(MainContext);
  const isAuthenticated = useFirebaseUser();
  let isValidToken = getAccount(session)?.thWriteAccessToken;

  const [cachedPostsLoaded, setCachedPostsLoaded] = useState(false);
  const miscellaneousContext: any = useMiscellaneousContext();
  const clearCache = useRef(false);

  // prefill saved filters from localstorage
  useEffect(() => {
    const savedFilters = localStorage.getItem(ENGAGE_FILTER_KEY);
    if (savedFilters) {
      const filtersJSON = JSON.parse(savedFilters);
      const { minFollowers, maxFollowers, minDaysOld, maxDaysOld } =
        filtersJSON;
      setNbMinDaysOld(minDaysOld || nbMinDaysOld);
      setNbMaxDaysOld(maxDaysOld || nbMaxDaysOld);
      setNbMinFollowers(minFollowers);
      setNbMaxFollowers(maxFollowers);
      setFilters(filtersJSON);
    }
  }, []);

  // track filter changes
  useEffect(() => {
    if (
      nbMinFollowers !== defaultMinFollowers ||
      nbMaxFollowers !== defaultMaxFollowers
    ) {
      setHasCustomFilters(true);
    } else {
      setHasCustomFilters(false);
    }
  }, [nbMinFollowers, nbMaxFollowers]);

  useEffect(() => {
    if (isAuthenticated && !hasFetched && isValidToken) {
      setHasFetched(true);
      sendRequest(session);
      setNoEngage(getAccount(session)?.noEngage ?? false);

      updateStatsCounter("access_engage");
    }
  }, [session, isAuthenticated]);

  const sendRequest = async (session) => {
    console.log("sendRequest:");

    if (isSending && !clearCache.current) return;

    setIsSending(true);

    miscellaneousContext.setIsDataRefreshing(true);

    let cachedPosts: any = await getCachedKey("cachedBestForYouPosts");
    let cachedPostsTime = await getCachedKeyTime("cachedBestForYouPostsTime");
    const aDayFromNow = new Date().getTime() + 24 * 60 * 60 * 1000;

    if (Boolean(cachedPostsTime) === false) {
      await updateCachedKeyTime("cachedBestForYouPostsTime", aDayFromNow);
    }

    if (
      new Date().getTime() <= new Date(cachedPostsTime).getTime() &&
      !clearCache.current
    ) {
      if (Boolean(cachedPosts) === true && cachedPosts?.tweets?.length > 0) {
        console.log("cache hit");
        setCachedPostsLoaded(true);
        const repliedTweets = await getCachedKey("allRepliedTweets");
        if (Boolean(repliedTweets) === true && repliedTweets?.tweets?.length > 0) {
          const filteredTweets = cachedPosts?.tweets?.filter((tweet) => !repliedTweets.tweets.includes(tweet.id_str)) ?? cachedPosts?.tweets;
          cachedPosts.tweets = [...filteredTweets];
        }
        setData(cachedPosts);
        setHasFetched(true);
        setIsSending(false);
        miscellaneousContext.setIsDataRefreshing(false);
        return;
      } else {
        console.log("cache miss");
        setCachedPostsLoaded(false);
      }
    }

    if (!cachedPostsLoaded || clearCache.current) {
      console.log("caching...");
      let getFilters: any = localStorage.getItem(ENGAGE_FILTER_KEY);

      if (getFilters) getFilters = JSON.parse(getFilters);

      let params: any = {
        isPersonalizedSorting: isPersonalizedSorting,
        nbMinDaysOld: getFilters?.minDaysOld || nbMinDaysOld,
        nbMaxDaysOld: getFilters?.maxDaysOld || nbMaxDaysOld,
        nbMinLikes: nbMinLikes,
        nbMinRt: nbMinRt,
        nbMinFollowers: getFilters?.minFollowers || defaultMinFollowers,
        nbMaxFollowers: getFilters?.maxFollowers || defaultMaxFollowers,
        noHashtags: getFilters?.noHashtags || false,
        notLiked: getFilters?.notLiked || false,
        noMediaAndLinks: getFilters?.noMediaAndLinks || false,
        positiveKeywords: getFilters?.positiveKeywords || [],
        negativeKeywords: getFilters?.negativeKeywords || [],
        count: 50,
        mode: isOrg ? "org" : "foryou",
        org: getAccount(session)?.subscription?.org,
      };

      //@ts-ignore
      let data = await grow(session, params, mainContext.onOpenUpgrade);

      if (!data || (data.success === 0 && !data.tweets)) {
        setData(null);
        await updateCachedKey("cachedBestForYouPosts", null);
      } else {
        data.tweets.forEach((tweet) => {
          tweet.isPosting = false;
          formatMedia(tweet);
        });
        setData(data);
        await updateCachedKey("cachedBestForYouPosts", data);
      }

      updateOnboardSteps("engage");
      setIsSending(false);
      setCachedPostsLoaded(false);
      await updateCachedKeyTime("cachedBestForYouPostsTime", aDayFromNow);
      miscellaneousContext.setIsDataRefreshing(false);

    }
  };

  const onSort = (sortedData) => {
    if (sortedData?.length) {
      const finalSortedData = {
        ...data,
        tweets: [...sortedData],
      };

      setData(finalSortedData);
    }
  };

  const onFilter = (filterData) => {
    setFilters(filterData);
    onRefresh([]);
  };

  const onRefresh = async (sortedFilteredData) => {
    setIsSending(false);
    clearCache.current = true;
    sendRequest &&
      (await sendRequest(
        session,
        //@ts-ignore
        getAccount(session)?.keywords?.join(",")
      ));
  };

  useEffect(() => {
    if (listId !== "" && getAccount(session)?.thWriteAccessToken) {
      sendRequest(session);
    }
  }, [listId]);

  return (
    <Grid maxW="800px" margin="auto">
      <Flex textStyle={textStyle["body.medium.light"]} alignItems="center">
        {header ?? (
          <Flex>
            <Text textStyle={textStyle["body.medium.light"]} fontSize="md">
              Engage with recent, relevant posts we found for you.
            </Text>
          </Flex>
        )}

      </Flex>
      <FilterSortRefreshAutoLike
        session={session}
        isAuthenticated={isAuthenticated}
        data={data?.tweets}
        onSortCallback={onSort}
        onFilterCallback={onFilter}
        mode={isOrg ? "org" : "bestforyou"}
        onRefreshButtonClickCallBack={onRefresh}
        filters={filters}
        isLoadingPosts={isSending}
        isRefreshSecondary={true}
        animateRefresh={true}
      />
      {isSending && !clearCache.current && (
        <Center pt={10}>
          <PuffLoader
            color={"#0A66C2"}
            loading={isSending}
            cssOverride={{
              display: "block",
              margin: "0 auto",
              borderColor: "red",
            }}
            size={50}
          />
        </Center>
      )}
      {(!isSending || clearCache.current) && isValidToken && (
        <Flex flexDirection="column">
          {
            //@ts-ignore
            data?.tweets?.length ? (
              <Box pt="3" w="100%" maxW="800px">
                <Center>
                  <TweetsReplies
                    data={data}
                    setData={setData}
                    refresher={refresher}
                    linkedinPostProps={{
                      disableFollowersCount: isOrg ? true : false,
                      disableIntro: isOrg ? true : false,
                      disableUserPopup: isOrg ? true : false,
                    }}
                  />
                </Center>
              </Box>
            ) : null
          }
          {!data?.tweets?.length && (
            <Center pt={10}>
              <Flex flexDirection="column" alignItems="center">
                <Text textStyle={textStyle["body.medium.light"]}>
                  No LinkedIn posts found matching your filters. Try adjusting your filters or refreshing to find more posts to engage with.
                </Text>
              </Flex>
            </Center>
          )}
        </Flex>
      )}
      <ConfirmPopup
        isOpen={isOpen}
        onClose={onClose}
        title={noEngage ? "Opt in engage" : "Opt out from engage"}
        body={
          noEngage
            ? "This will add your account in the list of posts to engage with so more people can engage with your content."
            : "This will remove your account from the list of posts to engage with so you will not take any benefit from this extra engagement."
        }
        callback={async () => {
          setNoEngage(!noEngage);
          await updateUser(session, { noEngage: !noEngage });
          toast.success("settings updated");
        }}
      />
    </Grid>
  );
}
